// API de los **moodboards**: carpetas de fotos de referencia de una marca —
// ADR-2026-09-13-moodboards-carpetas-de-referencia.
//
// Tabla `moodboards` (la carpeta):
//   id (uuid) · id_company (uuid) · brand_id (uuid, nullable) · nombre (text)
//   created_by (uuid) · created_at · updated_at
//
// Tabla `moodboard_imagenes` (las fotos de esa carpeta):
//   id (uuid) · moodboard_id (uuid) · id_company (uuid) · nombre_archivo (text)
//   orden (int) · created_at
//
// Storage bucket `moodboards` (PRIVADO, igual que `modelos` y
// `fondos_personalizados`):
//   `<idCompany>/<moodboardId>/<nombreArchivo>`
// Las URLs siempre se firman (TTL 1h).

import { supabase } from '../../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../utils/imageCache';
import {
  normalizeImageForUpload,
  UnsupportedImageFormatError,
} from '../../utils/heicConvert';

export const MOODBOARDS_BUCKET = 'moodboards';
const SIGNED_TTL = 3600;

/**
 * Miniatura de las fotos que asoman en el ícono de la carpeta. Se dibujan a
 * ~34px de lado, así que 240 alcanza para pantallas @3x.
 */
const PORTADA_PX = 240;
/**
 * Miniatura de la grilla de adentro de la carpeta. Los tiles miden hasta ~210px
 * de ancho, así que 640 cubre @3x sin traer el original.
 */
const GRILLA_PX = 640;

/** Cuántas fotos asoman en el ícono de la carpeta. */
export const FOTOS_EN_PORTADA = 3;

export interface ColorDeCarpeta {
  id: string;
  etiqueta: string;
  /** La pestaña y el cuerpo de atrás. */
  fondo: string;
  /** El frente — un paso más saturado, que es lo que le da el volumen. */
  frente: string;
}

/**
 * Los colores que puede tener una carpeta —
 * ADR-2026-09-13-moodboards-color-icono-cerrado-y-solo-escritorio.
 *
 * Pasteles a propósito: el ícono es grande y se repite en toda la grilla, así que
 * un color saturado la convertiría en un semáforo. Cada uno trae sus DOS tonos
 * porque la carpeta tiene dos planos; sacarle el frente por opacidad daría un gris
 * distinto según el fondo de la pantalla.
 *
 * En la base se guarda el `id`, no el hex (ver la migración
 * `20260913160000_moodboards_color.sql`): así estos tonos se pueden retocar sin
 * tocar una sola fila.
 */
export const COLORES_DE_CARPETA: ColorDeCarpeta[] = [
  { id: 'azul', etiqueta: 'Azul', fondo: '#CBD8FB', frente: '#A7BDF7' }, // hex-de-marca-ok: color de moodboard que elige el usuario
  { id: 'lavanda', etiqueta: 'Lavanda', fondo: '#DCD3FB', frente: '#BFB0F5' },
  { id: 'rosa', etiqueta: 'Rosa', fondo: '#FBD2E0', frente: '#F5AFC6' },
  { id: 'durazno', etiqueta: 'Durazno', fondo: '#FBDCC7', frente: '#F6BE9C' },
  { id: 'arena', etiqueta: 'Arena', fondo: '#F7E7BE', frente: '#EFD48D' },
  { id: 'verde', etiqueta: 'Verde', fondo: '#CCEBD5', frente: '#A4DCB5' },
  { id: 'agua', etiqueta: 'Agua', fondo: '#C8E9EE', frente: '#9BD6DF' },
  { id: 'piedra', etiqueta: 'Piedra', fondo: '#DEE2E9', frente: '#C0C7D4' },
];

/** El color de las carpetas que nunca eligieron uno. Es el primero de la lista. */
export const COLOR_POR_DEFECTO = COLORES_DE_CARPETA[0];

/**
 * El color de un `id` guardado. Un `id` desconocido —una carpeta vieja, o un tono
 * que se sacó del catálogo— cae al default en vez de dejar el ícono sin pintar.
 */
export function colorDeCarpeta(id: string | null | undefined): ColorDeCarpeta {
  return COLORES_DE_CARPETA.find((c) => c.id === id) ?? COLOR_POR_DEFECTO;
}

/** Cuántas fotos se firman de una tanda al pedir miniaturas. */
const CONCURRENCIA_FIRMAS = 8;

export interface MoodboardFoto {
  id: string;
  /** Nombre del objeto dentro de la carpeta del bucket. */
  nombreArchivo: string;
  /** Path completo en el bucket — el que hay que firmar o borrar. */
  path: string;
  createdAt: string;
  /** Signed URL de la miniatura (transformación de Supabase), para la grilla. */
  thumbUrl: string;
}

export interface Moodboard {
  id: string;
  nombre: string;
  brandId: string | null;
  /** ID del color elegido. `null` = el default (ver {@link colorDeCarpeta}). */
  color: string | null;
  cantidadFotos: number;
  /**
   * Hasta {@link FOTOS_EN_PORTADA} miniaturas firmadas —las últimas subidas—,
   * que son las que asoman dentro del ícono de la carpeta. Vacío = carpeta
   * vacía, y el ícono se dibuja sin fotos.
   */
  portada: string[];
  createdAt: string;
}

/**
 * Path de una foto en el bucket.
 *
 * El primer segmento es el `id_company` **porque de él depende la policy de
 * `storage.objects`** (`storage.foldername(name)[1]`): cambiarlo de lugar deja
 * de dar permiso.
 */
export function pathDeFoto(
  companyId: string,
  moodboardId: string,
  nombreArchivo: string,
): string {
  return `${companyId}/${moodboardId}/${nombreArchivo}`;
}

/**
 * Nombre del objeto. No lleva slug del nombre de la carpeta a propósito: el
 * nombre de la carpeta se puede editar y el del objeto no —quedaría mintiendo—,
 * y acá el archivo nunca se le muestra al usuario.
 */
export function nombreDeArchivo(file: File): string {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  // La extensión entra al path: si viniera con algo raro (un nombre con `/`,
  // una query pegada) rompería el path o la policy.
  const extLimpia = /^[a-z0-9]{1,5}$/.test(ext) ? ext : 'jpg';
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extLimpia}`;
}

/**
 * Firma miniaturas en tandas.
 *
 * De a una y no con `createSignedUrls` (plural) porque el pedido en lote **no
 * acepta `transform`**: la transformación viaja DENTRO del token de la firma, y
 * sin ella cada tile de la grilla bajaría la foto original (una carpeta de 40
 * referencias de 3 MP eran ~120 MP a decodificar de golpe — el mismo episodio
 * que documentan `fondosApi` y `modelsApi`). Para no dispararlas todas juntas
 * van de a {@link CONCURRENCIA_FIRMAS}.
 *
 * La caché de firmas se comparte con el resto de la app, pero con la clave
 * prefijada por el tamaño: la misma foto tiene una firma distinta por cada
 * transformación, y una sola clave por path las pisaría entre sí.
 */
async function firmarMiniaturas(
  paths: string[],
  px: number,
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const pendientes: string[] = [];
  for (const path of paths) {
    const clave = `moodboards:${px}:${path}`;
    const cacheada = getCachedSignedUrl(clave);
    if (cacheada) out.set(path, cacheada);
    else if (!pendientes.includes(path)) pendientes.push(path);
  }

  for (let i = 0; i < pendientes.length; i += CONCURRENCIA_FIRMAS) {
    const tanda = pendientes.slice(i, i + CONCURRENCIA_FIRMAS);
    const firmadas = await Promise.all(
      tanda.map(async (path) => {
        const { data } = await supabase.storage
          .from(MOODBOARDS_BUCKET)
          .createSignedUrl(path, SIGNED_TTL, {
            transform: { width: px, height: px, resize: 'contain', quality: 75 },
          });
        return [path, data?.signedUrl ?? ''] as const;
      }),
    );
    for (const [path, url] of firmadas) {
      // Sin URL el objeto no existe (una fila que quedó apuntando a nada). Se
      // omite del mapa y quien llama decide: la grilla dibuja el placeholder.
      if (!url) continue;
      out.set(path, url);
      setCachedSignedUrl(`moodboards:${px}:${path}`, url);
    }
  }
  return out;
}

/**
 * Firma UNA foto a resolución completa. Se usa recién al abrir el visor, no al
 * listar: la grilla se dibuja con miniaturas y firmar los originales de toda la
 * carpeta por adelantado sería traer lo que casi nunca se mira.
 */
export async function firmarFotoCompleta(path: string): Promise<string> {
  const cacheada = getCachedSignedUrl(path);
  if (cacheada) return cacheada;
  const { data } = await supabase.storage
    .from(MOODBOARDS_BUCKET)
    .createSignedUrl(path, SIGNED_TTL);
  const url = data?.signedUrl ?? '';
  if (url) setCachedSignedUrl(path, url);
  return url;
}

/**
 * Las carpetas de una marca, de la más nueva a la más vieja, con su contador de
 * fotos y las miniaturas de la portada.
 *
 * `brandId` filtra por la marca activa **e incluye las de `brand_id` nulo**: una
 * carpeta a la que le falte el vínculo con la marca tiene que seguir viéndose
 * (mismo criterio que los productos huérfanos, ADR-018). Con `brandId` en `null`
 * —la marca todavía no resolvió— devuelve todas las de la empresa.
 */
export async function fetchMoodboards({
  companyId,
  brandId,
}: {
  companyId: string;
  brandId: string | null;
}): Promise<Moodboard[]> {
  let query = supabase
    .from('moodboards')
    .select('id, nombre, brand_id, color, created_at')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  if (brandId) query = query.or(`brand_id.eq.${brandId},brand_id.is.null`);

  const { data: carpetas, error } = await query;
  if (error) throw error;
  if (!carpetas?.length) return [];

  const ids = carpetas.map((c) => c.id as string);
  // Todas las fotos de todas las carpetas en UN pedido, y el recorte a la
  // portada se hace acá: una query por carpeta serían N vueltas al servidor
  // para dibujar una pantalla que ya es una sola grilla.
  const { data: fotos, error: errFotos } = await supabase
    .from('moodboard_imagenes')
    .select('id, moodboard_id, nombre_archivo, created_at')
    .in('moodboard_id', ids)
    .order('orden', { ascending: true })
    .order('created_at', { ascending: false });
  if (errFotos) throw errFotos;

  const porCarpeta = new Map<string, { path: string }[]>();
  for (const foto of fotos ?? []) {
    const carpetaId = foto.moodboard_id as string;
    const lista = porCarpeta.get(carpetaId) ?? [];
    lista.push({
      path: pathDeFoto(companyId, carpetaId, foto.nombre_archivo as string),
    });
    porCarpeta.set(carpetaId, lista);
  }

  const pathsDePortada = ids.flatMap((id) =>
    (porCarpeta.get(id) ?? []).slice(0, FOTOS_EN_PORTADA).map((f) => f.path),
  );
  const firmas = await firmarMiniaturas(pathsDePortada, PORTADA_PX);

  return carpetas.map((c) => {
    const lista = porCarpeta.get(c.id as string) ?? [];
    return {
      id: c.id as string,
      nombre: c.nombre as string,
      brandId: (c.brand_id as string | null) ?? null,
      color: (c.color as string | null) ?? null,
      cantidadFotos: lista.length,
      portada: lista
        .slice(0, FOTOS_EN_PORTADA)
        .map((f) => firmas.get(f.path))
        .filter((url): url is string => Boolean(url)),
      createdAt: c.created_at as string,
    };
  });
}

/** Una carpeta con lo justo para nombrarla: sin portada ni firmas. */
export interface MoodboardResumen {
  id: string;
  nombre: string;
  cantidadFotos: number;
}

/**
 * Las carpetas de la marca SIN firmar nada: para el catálogo que el Agente lleva en el
 * prompt y para que `ver_moodboard` las liste
 * (ADR-2026-09-13-el-agente-ve-los-moodboards). `fetchMoodboards` firma las portadas y
 * eso es una request por foto; acá son dos consultas y listo. Mismo criterio de marca
 * que allá: la activa más las de `brand_id` nulo.
 */
export async function listarMoodboards({
  companyId,
  brandId,
}: {
  companyId: string;
  brandId: string | null;
}): Promise<MoodboardResumen[]> {
  let query = supabase
    .from('moodboards')
    .select('id, nombre')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  if (brandId) query = query.or(`brand_id.eq.${brandId},brand_id.is.null`);
  const { data: carpetas, error } = await query;
  if (error) throw error;
  if (!carpetas?.length) return [];
  const ids = carpetas.map((c) => c.id as string);
  const { data: fotos, error: errFotos } = await supabase
    .from('moodboard_imagenes')
    .select('moodboard_id')
    .in('moodboard_id', ids);
  if (errFotos) throw errFotos;
  const cuenta = new Map<string, number>();
  for (const f of fotos ?? []) {
    const id = f.moodboard_id as string;
    cuenta.set(id, (cuenta.get(id) ?? 0) + 1);
  }
  return carpetas.map((c) => ({
    id: c.id as string,
    nombre: c.nombre as string,
    cantidadFotos: cuenta.get(c.id as string) ?? 0,
  }));
}

/** Las fotos de una carpeta, de la más nueva a la más vieja, ya firmadas. */
export async function fetchFotos({
  companyId,
  moodboardId,
}: {
  companyId: string;
  moodboardId: string;
}): Promise<MoodboardFoto[]> {
  const { data, error } = await supabase
    .from('moodboard_imagenes')
    .select('id, nombre_archivo, created_at')
    .eq('moodboard_id', moodboardId)
    .order('orden', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;

  const filas = (data ?? []).map((f) => ({
    id: f.id as string,
    nombreArchivo: f.nombre_archivo as string,
    path: pathDeFoto(companyId, moodboardId, f.nombre_archivo as string),
    createdAt: f.created_at as string,
  }));
  const firmas = await firmarMiniaturas(
    filas.map((f) => f.path),
    GRILLA_PX,
  );
  return filas.map((f) => ({ ...f, thumbUrl: firmas.get(f.path) ?? '' }));
}

/** Crea una carpeta vacía y la devuelve lista para meter en la grilla. */
export async function crearMoodboard({
  companyId,
  brandId,
  nombre,
  color,
  userId,
}: {
  companyId: string;
  brandId: string | null;
  nombre: string;
  color?: string | null;
  userId?: string | null;
}): Promise<Moodboard> {
  const { data, error } = await supabase
    .from('moodboards')
    .insert({
      id_company: companyId,
      brand_id: brandId,
      nombre: nombre.trim() || 'Carpeta sin nombre',
      color: color ?? null,
      created_by: userId ?? null,
    })
    .select('id, nombre, brand_id, color, created_at')
    .single();
  if (error) throw error;
  return {
    id: data.id as string,
    nombre: data.nombre as string,
    brandId: (data.brand_id as string | null) ?? null,
    color: (data.color as string | null) ?? null,
    cantidadFotos: 0,
    portada: [],
    createdAt: data.created_at as string,
  };
}

/** Cambia el color del ícono. El `id` tiene que ser uno de {@link COLORES_DE_CARPETA}. */
export async function cambiarColorDeMoodboard(
  moodboardId: string,
  color: string,
): Promise<void> {
  const { error } = await supabase
    .from('moodboards')
    .update({ color, updated_at: new Date().toISOString() })
    .eq('id', moodboardId);
  if (error) throw error;
}

export async function renombrarMoodboard(
  moodboardId: string,
  nombre: string,
): Promise<void> {
  const { error } = await supabase
    .from('moodboards')
    .update({ nombre: nombre.trim(), updated_at: new Date().toISOString() })
    .eq('id', moodboardId);
  if (error) throw error;
}

/**
 * Borra la carpeta con todas sus fotos.
 *
 * Los objetos del bucket se borran ANTES que la fila: el `on delete cascade` se
 * lleva las filas de `moodboard_imagenes` pero **no toca el storage**, así que
 * al revés quedarían los archivos huérfanos y sin nada que los nombre.
 */
export async function eliminarMoodboard({
  companyId,
  moodboardId,
}: {
  companyId: string;
  moodboardId: string;
}): Promise<void> {
  const { data: fotos } = await supabase
    .from('moodboard_imagenes')
    .select('nombre_archivo')
    .eq('moodboard_id', moodboardId);
  const paths = (fotos ?? []).map((f) =>
    pathDeFoto(companyId, moodboardId, f.nombre_archivo as string),
  );
  if (paths.length) {
    await supabase.storage.from(MOODBOARDS_BUCKET).remove(paths);
  }
  const { error } = await supabase
    .from('moodboards')
    .delete()
    .eq('id', moodboardId);
  if (error) throw error;
}

/** Borra una foto: primero el objeto, después la fila (mismo orden y motivo). */
export async function eliminarFoto({
  fotoId,
  path,
}: {
  fotoId: string;
  path: string;
}): Promise<void> {
  await supabase.storage.from(MOODBOARDS_BUCKET).remove([path]);
  const { error } = await supabase
    .from('moodboard_imagenes')
    .delete()
    .eq('id', fotoId);
  if (error) throw error;
}

/** Tope de la foto que se copia desde otra parte de la app. Es holgado a propósito: son
 *  fotos que ya viven en nuestro storage (una generada, la foto de un producto). */
const MAX_BYTES_DESDE_URL = 25 * 1024 * 1024;

const EXTENSIONES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/heic': 'heic',
};

/**
 * Sube a la carpeta una imagen que YA vive en la app, por su URL firmada — una
 * generada, la foto de un producto o de un modelo, el logo, una foto de otro moodboard
 * (ADR-2026-09-13-el-agente-arma-moodboards).
 *
 * Baja los bytes y los vuelve a subir en vez de copiar el objeto entre buckets: las
 * fuentes viven en cuatro buckets con estructuras de path distintas, y este camino pasa
 * por `subirFoto`, o sea que hereda la normalización de formato y la fila en
 * `moodboard_imagenes` sin duplicar nada de eso.
 */
export async function subirFotoDesdeUrl({
  companyId,
  moodboardId,
  url,
}: {
  companyId: string;
  moodboardId: string;
  url: string;
}): Promise<MoodboardFoto> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`no se pudo bajar la imagen (${res.status})`);
  const blob = await res.blob();
  if (blob.size === 0) throw new Error('la imagen vino vacía');
  if (blob.size > MAX_BYTES_DESDE_URL) throw new Error('la imagen pesa demasiado');
  const tipo = blob.type || 'image/jpeg';
  const ext = EXTENSIONES[tipo] ?? 'jpg';
  const file = new File([blob], `copia.${ext}`, { type: tipo });
  return subirFoto({ companyId, moodboardId, file });
}

/**
 * Sube un archivo a la carpeta y devuelve la foto lista para la grilla.
 *
 * Normaliza el formato (HEIC/HEIF/AVIF/WEBP → JPEG) como red de seguridad de la
 * capa de API, igual que `uploadFondo`: cualquier caller queda cubierto aunque
 * no convierta antes, y `ext`/`contentType` se calculan sobre el archivo final.
 */
export async function subirFoto({
  companyId,
  moodboardId,
  file,
}: {
  companyId: string;
  moodboardId: string;
  file: File;
}): Promise<MoodboardFoto> {
  let normalizado: File;
  try {
    normalizado = await normalizeImageForUpload(file);
  } catch (err) {
    // Formato bloqueado (RAW/TIFF): su mensaje ya dice qué pasa y se re-lanza
    // tal cual. El genérico queda sólo para fallos reales de conversión.
    if (err instanceof UnsupportedImageFormatError) throw err;
    console.error('[moodboardsApi] conversión de formato falló', err);
    throw new Error(
      'No pudimos convertir la imagen (HEIC/AVIF). Probá con una imagen en formato png o jpg.',
    );
  }

  const nombre = nombreDeArchivo(normalizado);
  const path = pathDeFoto(companyId, moodboardId, nombre);
  const { error: errSubida } = await supabase.storage
    .from(MOODBOARDS_BUCKET)
    .upload(path, normalizado, {
      cacheControl: '3600',
      upsert: false,
      contentType: normalizado.type || 'image/jpeg',
    });
  if (errSubida) throw errSubida;

  const { data, error } = await supabase
    .from('moodboard_imagenes')
    .insert({
      moodboard_id: moodboardId,
      id_company: companyId,
      nombre_archivo: nombre,
    })
    .select('id, created_at')
    .single();
  if (error) {
    // La fila no entró: el objeto que se acaba de subir no lo nombra nadie más,
    // así que se limpia acá en vez de quedar ocupando el bucket para siempre.
    await supabase.storage.from(MOODBOARDS_BUCKET).remove([path]);
    throw error;
  }

  const firmas = await firmarMiniaturas([path], GRILLA_PX);
  return {
    id: data.id as string,
    nombreArchivo: nombre,
    path,
    createdAt: data.created_at as string,
    thumbUrl: firmas.get(path) ?? '',
  };
}
